/**
 * joinInvite.js — what the student join page (`/join?code=…`) decides. Pure, so
 * every rule here is tested without a browser.
 *
 * The server half is vc_be/utils/studentInvite.js: it names each refusal with a
 * `code`, and answers with `joinUrl` — the institute's OWN address (its custom
 * domain, else its subdomain). `MIN_PASSWORD_LENGTH` must match it.
 */
import { phoneProblem } from "../../../utils/phone";

export const MIN_PASSWORD_LENGTH = 6;

/** Added to the URL when the page moves the student, so it moves at most once. */
export const MOVED_PARAM = "moved";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isLocalHost = (h) => h === "localhost" || h === "127.0.0.1" || h.endsWith(".localhost");

/** The `code` query parameter, trimmed — "" when absent. */
export function inviteCodeFrom(search) {
  return (new URLSearchParams(search).get("code") || "").trim();
}

/**
 * Where to send the browser, or null to stay.
 *
 * The invite belongs on the institute's own address: that is where the
 * student's session has to live (they will sign in there tomorrow), and where
 * the page wears the institute's brand from the first frame. A link opened on
 * our own host — every link handed out before the server built them this way —
 * or on another institute's host is moved there once. A dev host never moves.
 */
export function moveTarget(joinUrl, { hostname, search }) {
  if (!joinUrl || isLocalHost(String(hostname || "").toLowerCase())) return null;
  let target;
  try {
    target = new URL(joinUrl);
  } catch {
    return null;
  }
  if (target.protocol !== "https:" && target.protocol !== "http:") return null;
  if (target.hostname.toLowerCase() === String(hostname || "").toLowerCase()) return null;
  if (new URLSearchParams(search).get(MOVED_PARAM)) return null;
  target.searchParams.set(MOVED_PARAM, "1");
  return target.toString();
}

/**
 * Every state the card can show instead of the form. Keys are the server's
 * refusal codes, plus three of the page's own.
 */
export const INVITE_STATUSES = {
  INVITE_MISSING: "INVITE_MISSING",
  INVITE_INVALID: "INVITE_INVALID",
  INVITE_DISABLED: "INVITE_DISABLED",
  INVITE_EXPIRED: "INVITE_EXPIRED",
  INSTITUTE_CLOSED: "INSTITUTE_CLOSED",
  INVITE_OTHER_INSTITUTE: "INVITE_OTHER_INSTITUTE",
  NETWORK: "NETWORK",
  ACCOUNT_CREATED: "ACCOUNT_CREATED",
};

/** A server refusal code → the status the card shows. Anything unknown is "invalid". */
export const statusOf = (code) =>
  INVITE_STATUSES[code] && code !== "ACCOUNT_CREATED" && code !== "NETWORK" ? code : "INVITE_INVALID";

/**
 * What the card says for a status.
 *   tone        info | warning | danger | success
 *   askForCode  offer a box for the link or the code (it may have been mangled)
 *   retry       offer "Try again"
 *   signIn      "Sign in" is THE action rather than a footnote
 */
export function statusCopy(status, brandName) {
  const who = brandName || "your institute";
  const Who = brandName || "Your institute";
  switch (status) {
    case "INVITE_MISSING":
      return {
        tone: "info",
        title: "Open your invite link",
        body: "This page needs the invite link your institute shared with you. Open it again from the message, or paste the link or the code below.",
        askForCode: true,
      };
    case "INVITE_DISABLED":
      return {
        tone: "warning",
        title: "This invite link is switched off",
        body: `${Who} has paused sign-ups through this link. Ask them for a new one — or sign in if you already have an account.`,
      };
    case "INVITE_EXPIRED":
      return {
        tone: "warning",
        title: "This invite link has expired",
        body: `Ask ${who} to share a fresh link. If you have already joined, just sign in.`,
      };
    case "INSTITUTE_CLOSED":
      return {
        tone: "info",
        title: "Sign-ups are closed for now",
        body: "This institute is not accepting new students at the moment. Please check with them directly.",
      };
    case "INVITE_OTHER_INSTITUTE":
      return {
        tone: "warning",
        title: "This link is for a different institute",
        body: "Open the invite link exactly as it was shared with you, so you join the right place.",
      };
    case "NETWORK":
      return {
        tone: "danger",
        title: "We couldn't check your invite",
        body: "Check your internet connection and try again.",
        retry: true,
      };
    case "ACCOUNT_CREATED":
      return {
        tone: "success",
        title: "Your account is ready",
        body: "Sign in with your mobile number and the password you just chose.",
        signIn: true,
      };
    case "INVITE_INVALID":
    default:
      return {
        tone: "danger",
        title: "We couldn't find this invite",
        body: "The link may have been copied only in part, or replaced by a newer one. Paste the full link or the code below, or ask your institute for the current link.",
        askForCode: true,
      };
  }
}

/** Whatever was pasted into the "link or code" box → the code. */
export function codeFromInput(text) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  const match = raw.match(/[?&]code=([^&#\s]+)/i);
  if (match) {
    try {
      return decodeURIComponent(match[1]).trim();
    } catch {
      return match[1].trim();
    }
  }
  return raw.replace(/\s+/g, "");
}

export const EMPTY_FORM = { name: "", mobile: "", email: "", password: "", confirmPassword: "" };

/** The order the fields appear in — the first one in error takes focus. */
export const FIELD_ORDER = ["name", "mobile", "email", "password", "confirmPassword", "terms"];

/** The form → `{ field: message }` for every field that cannot be sent. */
export function joinFormErrors(form, acceptedTerms) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Enter your full name.";

  if (!form.mobile.trim()) errors.mobile = "Enter your mobile number.";
  else {
    const problem = phoneProblem(form.mobile);
    if (problem) errors.mobile = problem;
  }

  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) {
    errors.email = "Enter a valid email address, or leave it empty.";
  }

  if (form.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  } else if (form.confirmPassword !== form.password) {
    errors.confirmPassword = "Passwords don't match.";
  }

  if (!acceptedTerms) errors.terms = "Please accept the terms to continue.";
  return errors;
}

export const firstErrorField = (errors) => FIELD_ORDER.find((f) => errors[f]) || null;

export const joinPayload = (code, form) => ({
  code,
  name: form.name.trim(),
  email: form.email.trim() || undefined,
  mobile: form.mobile,
  password: form.password,
});

/**
 * A failed join → what the form does about it:
 *   { status }          the invite itself was refused — swap the form for the card
 *   { field, message }  one field is the problem
 *   { message, signIn } a notice above the button, offering "Sign in" when an
 *                       account already exists
 */
export function joinFailure(err) {
  if (!err?.response) {
    return { message: "We couldn't reach the server. Check your connection and try again." };
  }
  const data = err.response.data || {};
  if (data.code && INVITE_STATUSES[data.code]) return { status: statusOf(data.code) };
  if (data.code === "EMAIL_TAKEN") return { field: "email", message: data.message };
  if (data.code === "ALREADY_REGISTERED" || data.code === "ACCOUNT_EXISTS") {
    return { message: data.message, signIn: true };
  }
  return { message: data.message || "Something went wrong. Please try again." };
}

/**
 * Who is already signed in on this device, if anyone:
 *   null | { name, student: boolean }
 * Joining replaces that session, so the form says so before it happens.
 */
export function signedInAs(user, token) {
  if (!user || !token) return null;
  return {
    name: user.name || user.email || user.phone || "another account",
    student: user.usertype === "student",
  };
}
